// MUI JSX
import {
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Table,
  styled,
  Box,
  Typography,
} from "@mui/material";
// Custom JSX
import CircleIcon from "@mui/icons-material/Circle";
import {
  CriticatilityCellDiv,
  MoreCell,
  MoreRow,
  SpanActiveStatusStyled,
  SpanDeactiveStatusStyled,
  SpanEmailStyled,
  SpanIdStyled,
} from "../dataGrid.styled";
import CheckboxChecked from "assets/CheckboxChecked";
import CheckboxUnchecked from "assets/CheckboxUnchecked";
import GreenCheckCircle from "assets/GreenCheckCircle";
import { AssignedToDropDown, StatusChangeDropDown } from "./datagridRows";
import { useCallback, useState } from "react";
import CasesMedium from "assets/CasesMedium";
import CasesLow from "assets/CasesLow";
import CasesInformational from "assets/CasesInformational";
import CasesHigh from "assets/CasesHigh";
import { CasesEmergeny } from "assets/CasesEmergeny";
import { useTheme } from "styled-components";
import {
  ActionsCell,
  AssigneeCell,
  StatusCell,
  SeverityCell,
} from "modules/flywheel/components/flywheelIncidents/cellWithMemo";

export default function DataGridRow({
  t,
  row,
  headers,
  index,
  highlightedRowId,
  visibleKeys,
  isSelected,
  handleCheckBoxClick,
  isCheckBoxChecked,
  handleIdClick,
  maxWidths,
  statusMapping,
  user,
  handleSetStatus,
  users,
  handleAssignedTo,
  onActionClick,
}: any) {
  const theme = useTheme();
  
  // Extract tags from the row data
  const tags = Array.isArray(row.tags) ? row.tags : [];

  const PassFailBadge = styled("span")<{ status: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    padding: 2px 6px;
    border-radius: 12px;
    color: #ffffff;
    font-weight: 500;
    font-size: 0.75rem;
    background: ${({ status }) =>
      status === "PASSED"
        ? "#67a356"
        : status === "NOT_AVAILABLE"
        ? "#bdbdbd"
        : "#fa314a"};
  `;

  // Create a styled component that wraps TableCell
  const StyledTableCell = styled(TableCell)(() => ({
    color: theme.colors.textColor,
  }));

  const [showMore, setShowMore] = useState(false);
  const [tempAmendedAssignage, setTempAmendedAssignage] = useState();
  const [tempAmendedStatus, setTempAmendedStatus] = useState();
  const isItemSelected = isSelected(row.id as string);

  function localhandleAssignedTo(user, id) {
    handleAssignedTo(user, id);
    setTempAmendedAssignage(user);
  }
  const onAssignedTo = useCallback(
    (user, id) => localhandleAssignedTo(user, id),
    []
  );
  function localhandleSetStatus(status, rowId) {
    handleSetStatus(status, rowId);
    setTempAmendedStatus(status);
  }
  const onStatusAssignTo = useCallback(
    (user, id) => localhandleSetStatus(user, id),
    []
  );
  const renderCriticatilityCell = (
    value: string,
    key: string,
    index: number
  ) => {
    let icon;
    switch (value) {
      case "SEVERITY.INFORMATIVE":
        icon = <CasesInformational />;
        break;
      case "SEVERITY.OTHER":
        icon = <CasesInformational />;
        break;
      case "SEVERITY.LOW":
        icon = <CasesLow />;
        break;
      case "SEVERITY.MEDIUM":
        icon = <CasesMedium />;
        break;
      case "SEVERITY.HIGH":
        icon = <CasesHigh />;
        break;
      case "SEVERITY.CRITICAL":
        icon = <CasesEmergeny />;
        break;
      case "SEVERITY.FATAL":
        icon = <CasesEmergeny />;
        break;
      default:
        icon = null;
    }
    return (
      <StyledTableCell
        key={`${key}_${index}`}
        onClick={(event) => setShowMore(!showMore)}
      >
        <CriticatilityCellDiv>
          {icon} {t(value)}
        </CriticatilityCellDiv>
      </StyledTableCell>
    );
  };

  return (
    <>
      <TableRow
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        key={`data-grid-row-${row.id}-${index}`}
        selected={isItemSelected}
        sx={{ cursor: "pointer" }}
        className={highlightedRowId === row.id ? "selected-row" : ""}
      >
        {visibleKeys.map((key, index) => {
          if (key === "isSelected") {
            return (
              <StyledTableCell padding="checkbox" key={`${key}_${index}`}>
                <Checkbox
                  color="primary"
                  checked={isItemSelected}
                  icon={
                    <CheckboxUnchecked
                      selected={isCheckBoxChecked(row.id)}
                      theme={theme}
                    />
                  }
                  checkedIcon={<CheckboxChecked />}
                  onClick={(event) => handleCheckBoxClick(event, row.id)}
                />
              </StyledTableCell>
            );
          } else if (key === "severity" || key === "criticality") {
            const severityValue = row[key];
            let displayValue = severityValue;

            // Check if the value starts with "SEVERITY." and translate it
            if (
              typeof severityValue === "string" &&
              severityValue.startsWith("SEVERITY.")
            ) {
              displayValue = t(severityValue);
            }

            return (
              <SeverityCell
                name={row["findingName"]}
                key={key}
                value={displayValue}
                index={index}
              />
            );
          } else if (
            key === "falsePositiveProbability" ||
            key === "falsepositiveprobability"
          ) {
            // Get the value from the row (now provided by the mapper)
            const value =
              row[key] ||
              row["falsepositiveprobability"] ||
              row["falsePositiveProbability"];

            return (
              <StyledTableCell
                key={`${key}_${index}`}
                onClick={(event) => setShowMore(!showMore)}
              >
                {value}
              </StyledTableCell>
            );
          } else if (key === "resourceids") {
            return (
              <StyledTableCell
                key={`${key}_${index}`}
                sx={{
                  maxWidth: maxWidths.classification,
                }}
              >
                <SpanIdStyled>
                  {row[key] && typeof row[key] === "object"
                    ? Object.entries(row[key]).map(([key, value]: any) => (
                        <div key={key}>
                          {key}: {value}
                        </div>
                      ))
                    : row[key]}
                </SpanIdStyled>
              </StyledTableCell>
            );
          } else if (key === "passfail") {
            return (
              <StyledTableCell
                align="right"
                key={`${key}_${index}`}
                sx={{ background: "transparent" }}
                onClick={(event) => {
                  setShowMore(!showMore);
                  console.log(`Pass/Fail clicked for ID: ${row["id"]}`);
                  console.log(`Pass/Fail row: ${row}`);
                }}
              >
                <PassFailBadge status={row[key]}>
                  {row[key] === "PASSED"
                    ? "Pass"
                    : row[key] === "NOT_AVAILABLE"
                    ? "N/A"
                    : row[key] === "FAILED"
                    ? "Fail"
                    : row[key]}
                </PassFailBadge>
              </StyledTableCell>
            );
          } else if (key === "findingName" || key === "alertname") {
            return (
              <StyledTableCell
                onClick={(event) => {
                  handleIdClick(row["id"], row["findingid"]); // <-- use 'findingid' (lowercase)
                  console.log(`Finding Name clicked for ID: ${row["id"]}`);
                }}
                key={`${key}_${index}`}
                sx={{
                  maxWidth: maxWidths.classification,
                }}
              >
                <SpanIdStyled>{row[key]}</SpanIdStyled>
                {row["findingId"] && (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#888",
                      marginTop: "2px",
                    }}
                  >
                    ID: {row["findingId"]}
                  </div>
                )}
              </StyledTableCell>
            );
          } else if (key === "id") {
            return (
            <StyledTableCell
              onClick={(event) => {
                handleIdClick(row["id"], row["findingid"]); // <-- use 'findingid' (lowercase)
                console.log(`Finding Name clicked for ID: ${row["id"]}`);
              }}
              key={`${key}_${index}`}
              sx={{
                maxWidth: maxWidths.classification,
              }}
            >
                <SpanIdStyled>{row[key]}</SpanIdStyled>
              </StyledTableCell>
            );
          } else if (key === "caseId") {
            return (
              <StyledTableCell
                onClick={(event) => {
                  handleIdClick(row["caseId"], row["type"]);
                }}
                key={`${key}_${index}`}
                sx={{
                  maxWidth: maxWidths.classification,
                }}
              >
                <SpanIdStyled>{row[key]}</SpanIdStyled>
              </StyledTableCell>
            );
          } else if (key === "email") {
            return (
              <StyledTableCell
                key={`${key}_${index}`}
                sx={{
                  maxWidth: maxWidths.classification,
                }}
              >
                <SpanEmailStyled>{row[key]}</SpanEmailStyled>
              </StyledTableCell>
            );
          } else if (key === "status") {
            return (
              <StatusCell
                key={`${key}_${index}`}
                value={row[key]}
                statusMapping={statusMapping}
                handleSetStatus={onStatusAssignTo}
                rowId={row.id}
                userEmail={user?.email}
                rowEmail={row.email}
              />
            );
          } else if (key === "assignedto") {
            return (
              <AssigneeCell
                key={`${key}_${index}`}
                users={users}
                handleAssignedTo={onAssignedTo}
                rowId={row.id}
                defaultValue={row[key]}
              />
            );
          } else if (key === "due") {
            // Calculate due date progress
            const dueDate = new Date(row[key]);
            const today = new Date();
            const createdDateStr = row.createdDate;
            const createdDate = new Date(createdDateStr);

            const isOverdue = today > dueDate;
            const isClosed = row.status === "Closed";

            // Calculate progress percentage
            let progressPercentage = 0;

            if (isOverdue) {
              // If overdue, fill the bar completely
              progressPercentage = 100;
            } else {
              // Calculate elapsed time as a percentage of total time
              const totalTimeMs = dueDate.getTime() - createdDate.getTime();
              const elapsedTimeMs = today.getTime() - createdDate.getTime();
              progressPercentage = Math.min(
                100,
                Math.max(0, Math.floor((elapsedTimeMs / totalTimeMs) * 100))
              );
            }

            // Determine progress bar color
            let barColor = "#4caf50"; // Green - still time
            if (isOverdue) {
              barColor = isClosed ? "#9e9e9e" : "#f44336"; // Grey if closed, red if open
            }

            const barWidth = `${progressPercentage}%`;

            return (
              <StyledTableCell>
                <span>{row[key]}</span>
                <Box mt={0.5}>
                  <Box
                    height={6}
                    width="100px"
                    bgcolor="#eee"
                    borderRadius={4}
                    overflow="hidden"
                  >
                    <Box height="100%" width={barWidth} bgcolor={barColor} />
                  </Box>
                </Box>{" "}
              </StyledTableCell>
            );
          } else if (key === "actions") {
            return (
              <ActionsCell
                key={`${key}_${index}`}
                rowId={row.id}
                type={row.type}
                onActionClick={onActionClick}
              />
            );
          } else if (key === "health") {
            return (
              <StyledTableCell key={`${key}_${index}`}>
                <GreenCheckCircle />
              </StyledTableCell>
            );
          } else if (key === "dateTime") {
            return (
              <StyledTableCell align="left" key={`${key}_${index}`}>
                <div className="date-time">{row[key]}</div>
              </StyledTableCell>
            );
          } else if (key === "action") {
            return (
              <StyledTableCell align="center" key={`${key}_${index}`}>
                {row.passfail !== "PASSED" &&
                row.passfail !== "NOT_AVAILABLE" ? (
                  <button
                    onClick={(event) => {
                      // Call with id to trigger the view in the preview panel
                      event.stopPropagation(); // Prevent row click event
                      console.log("View button clicked for ID:", row["id"]);
                      if (typeof handleIdClick === "function") {
                        handleIdClick(row["id"]);
                      } else {
                        console.error("handleIdClick is not a function");
                      }
                    }}
                    style={{
                      color:
                        theme.name === "dark-theme"
                          ? "#FFFFFF"
                          : theme.colors.light,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                    }}
                  >
                    <span>View</span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "4px" }}
                    >
                      <path
                        d="M2.5 6H9.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 2.5L9.5 6L6 9.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : (
                  <span className="px-3">N/A</span>
                )}
              </StyledTableCell>
            );
          } else if (key === "riskScore") {
            // Get the risk score from the row (now provided by the mapper)
            const riskScore = row[key];

            return (
              <StyledTableCell
                align="right"
                key={`${key}_${index}`}
                onClick={(event) => setShowMore(!showMore)}
              >
                {riskScore}
              </StyledTableCell>
            );
          } else if (
            key !== "id" &&
            key !== "latestKeyEvent" &&
            key !== "entity"
          ) {
            // temporary condition to remove latest key event
            return (
              <StyledTableCell
                align="right"
                key={`${key}_${index}`}
                sx={
                  key === "classification"
                    ? {
                        maxWidth: maxWidths.classification,
                      }
                    : {}
                }
                onClick={(event) => setShowMore(!showMore)}
              >
                {row[key]}
              </StyledTableCell>
            );
          }
        })}
      </TableRow>

      {/* Render additional table row if clicked and additional info is to be shown */}
      {showMore && (
        <TableRow
          key={`ioc-row-more-${index}`}
          sx={{ "&:last-child td, &:last-child th": { border: 0 }, padding: 0 }}
        >
          <StyledTableCell colSpan={12} sx={{ padding: 0 }}>
            <Table>
              {/* Existing code for other rows, inject tags after Org */}
              {headers.map((header, headerIndex) => [
                <TableBody
                  key={`ioc-row-more-${headerIndex}`}
                  sx={{ borderBottom: "1px solid rgba(81, 81, 81, 1)" }}
                >
                  <MoreRow
                    key={`ioc-row-more-${headerIndex}-1`}
                    className="d-flex flex-row"
                  >
                    <MoreCell>
                      <span style={{ color: theme.colors.textDisabledColor }}>
                        {header.label}
                      </span>
                    </MoreCell>
                    <MoreCell>
                      {/* Print the value of the row as is, status and assignedto are special */}
                      {header.id === "resourceids" ? (
                        <span className="text-wrap">{row[header.id]}</span>
                      ) : (
                        <span
                          className="pt-1"
                          style={{
                            color: theme.colors.textColor,
                            whiteSpace: "pre-line",
                          }}
                        >
                          {tempAmendedStatus && header.id === "status" ? (
                            tempAmendedStatus
                          ) : tempAmendedAssignage &&
                            header.id === "assignedto" ? (
                            tempAmendedAssignage
                          ) : header.id === "passfail" ? (
                            <PassFailBadge status={row[header.id]}>
                              {row[header.id] === "PASSED"
                                ? "Pass"
                                : row[header.id] === "NOT_AVAILABLE"
                                ? "N/A"
                                : row[header.id] === "FAILED"
                                ? "Fail"
                                : row[header.id]}
                            </PassFailBadge>
                          ) : header.id === "falsePositiveProbability" ||
                            header.id === "falsepositiveprobability" ? (
                            (() => {
                              // Get the false positive probability value from the row
                              const value =
                                row[header.id] ||
                                row["falsepositiveprobability"] ||
                                row["falsePositiveProbability"];
                              const displayValue = value;

                              return <span>{displayValue}</span>;
                            })()
                          ) : header.id === "severity" ? (
                            (() => {
                              const severityValue = row[header.id];
                              let translatedValue = severityValue;
                              let icon: React.ReactNode = null;

                              if (
                                typeof severityValue === "string" &&
                                severityValue.startsWith("SEVERITY.")
                              ) {
                                translatedValue = t(severityValue);

                                // Set the appropriate icon based on severity
                                switch (severityValue) {
                                  case "SEVERITY.INFORMATIVE":
                                    icon = <CasesInformational />;
                                    break;
                                  case "SEVERITY.OTHER":
                                    icon = <CasesInformational />;
                                    break;
                                  case "SEVERITY.LOW":
                                    icon = <CasesLow />;
                                    break;
                                  case "SEVERITY.MEDIUM":
                                    icon = <CasesMedium />;
                                    break;
                                  case "SEVERITY.HIGH":
                                    icon = <CasesHigh />;
                                    break;
                                  case "SEVERITY.CRITICAL":
                                    icon = <CasesEmergeny />;
                                    break;
                                  case "SEVERITY.FATAL":
                                    icon = <CasesEmergeny />;
                                    break;
                                }
                              }

                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {icon && (
                                    <span style={{ marginRight: "5px" }}>
                                      {icon}
                                    </span>
                                  )}
                                  <span>{translatedValue}</span>
                                </div>
                              );
                            })()
                          ) : header.id === "riskScore" ? (
                            (() => {
                              // Get the risk score from the row
                              const riskScore = row["riskScore"];
                              return <span>{riskScore}</span>;
                            })()
                          ) : (
                            <p>{row[header.id]}</p>
                          )}
                        </span>
                      )}
                    </MoreCell>
                  </MoreRow>
                </TableBody>,
                // Inject tags row after Org
                header.id === "org" ? (
                  <>
                    <TableBody key="tags-row">
                      <MoreRow className="d-flex flex-row">
                        <MoreCell>
                          <span style={{ color: theme.colors.textDisabledColor }}>Tags</span>
                        </MoreCell>
                        <MoreCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Array.isArray(row.tags) && row.tags.length > 0 ? (
                              row.tags.map((tag, idx) => (
                                <Box
                                  key={`${tag.key}-${tag.value}-${idx}`}
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '12px',
                                    background: theme.name === 'dark-theme' ? '#bdbdbd' : '#D9D9D9',
                                    color: theme.name === 'dark-theme' ? '#FFFFFF' : '#000000',
                                    fontSize: '0.85rem',
                                    fontWeight: 400,
                                    mr: 1,
                                    mb: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      background: theme.name === 'dark-theme' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'
                                    }
                                  }}
                                >
                                  <span style={{ fontWeight: 500, color: theme.name === 'dark-theme' ? '#A0A0A0' : '#666666' }}>{tag.key}:</span>
                                  <span style={{ marginLeft: '4px' }}>{tag.value}</span>
                                </Box>
                              ))
                            ) : (
                              <span style={{ color: theme.colors.textDisabledColor }}>No tags available</span>
                            )}
                          </Box>
                        </MoreCell>
                      </MoreRow>
                    </TableBody>
                    {/* Divider line below tags */}
                    <tr>
                      <td colSpan={12} style={{ padding: 0 }}>
                        <Box sx={{ borderBottom: '1px solid #333', width: '100%', mt: 1, mb: 1 }} />
                      </td>
                    </tr>
                  </>
                ) : null
              ])}
            </Table>
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
}
